import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../SEO';
import { appConfig } from '../../config/appConfig';
import hvacHeroVisual from '../../images/Comp_3.gif';
import membershipVisual from '../../images/one.gif';
import retentionVisual from '../../images/three.gif';

const faqs = [
  {
    question: 'What EBITDA multiple can a residential HVAC company receive?',
    answer:
      'Well-run residential HVAC platforms with 20%+ EBITDA margins, 40%+ membership mix, and strong dispatch efficiency see 6-9x EBITDA. Multi-market operators with clean systems, financing penetration, and tech-enabled routing can command double-digit multiples from private equity roll-ups.',
  },
  {
    question: 'How important is a maintenance membership program to valuation?',
    answer:
      'Memberships provide recurring revenue, predictable load, and cross-sell opportunities. We highlight retention, auto-pay adoption, and attach rates to show stabilization of seasonality—critical for buyers underwriting premium multiples.',
  },
  {
    question: 'Can I sell if my financials are cash-basis?',
    answer:
      'Yes. We convert cash-basis books, normalize seasonality, and rebuild gross margin by segment. We also separate one-time project revenue from service revenue so buyers can evaluate stability.',
  },
  {
    question: 'Do HVAC buyers require the owner to stay involved?',
    answer:
      'Most private equity-backed aggregators expect a 12-24 month transition. We structure incentives for your leadership team, elevate your GM or COO into the narrative, and negotiate post-close roles that align with your lifestyle goals.',
  },
];

const structuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'HVAC & Home Services M&A Advisory',
    'name': 'HVAC & Home Services M&A Advisor',
    'provider': {
      '@type': 'Organization',
      'name': appConfig.site.organizationName,
      'url': appConfig.site.baseUrl,
    },
    'areaServed': ['United States'],
    'audience': {
      '@type': 'Audience',
      'name': 'Owners of HVAC, plumbing, electrical, and home services companies',
    },
    'url': `${appConfig.site.baseUrl}/hvac-home-services-mna-advisor`,
    'description':
      'Sell-side advisory for residential and commercial HVAC and home services contractors. We highlight membership mix, dispatch efficiency, and route density to maximize valuation.',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD',
      'availability': 'https://schema.org/InStock',
      'url': `${appConfig.site.baseUrl}/valuation`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  },
];

const heroMetrics = [
  {
    label: 'Route-Dense Buyers',
    value: '140+',
    description: 'PE-backed HVAC platforms, utilities, and strategics seeking multi-market scale.',
  },
  {
    label: 'Median EBITDA Lift',
    value: '30%',
    description: 'uplift captured once membership, dispatch, and financing data are investor-ready.',
  },
  {
    label: 'Readiness Sprint',
    value: '6 weeks',
    description: 'time to normalize cash-basis books and rebuild membership + dispatch analytics.',
  },
];

const valuationDrivers = [
  {
    title: 'Revenue Mix & Membership Engine',
    points: [
      'Membership penetration, renewal rates, and auto-pay adoption.',
      'Residential versus commercial mix and service agreements with inflation escalators.',
      'Financing adoption for replacements and cross-sell conversion from tune-ups.',
      'Seasonality smoothing via pre-booked maintenance and shoulder season campaigns.',
    ],
  },
  {
    title: 'Dispatch & Tech Density',
    points: [
      'Route density across territories and average miles per job.',
      'Technician utilization, conversion rates, and average ticket by call type.',
      'Digital scheduler adoption, capacity planning, and job-costing discipline.',
      'Call center answer speed, booking rates, and data capture quality.',
    ],
  },
  {
    title: 'Financial Performance',
    points: [
      'Gross margin by trade, replacement versus service, and inventory controls.',
      'EBITDA conversion after adjusting for owner compensation and above-market perks.',
      'Cash flow seasonality modeling tied to weather patterns and marketing campaigns.',
      'Working capital requirements, vendor terms, and fleet management costs.',
    ],
  },
];

const rollupDynamics = [
  'Private equity-backed HVAC roll-ups are expanding regionally to increase purchasing power, technology leverage, and brand recognition. They pay up for platforms with proven multi-market management.',
  'Strategic utilities and energy companies are acquiring service providers to deepen customer relationships and introduce subscription products that smooth revenue across seasons.',
  'Home services aggregators seek bolt-ons with specialty expertise—duct cleaning, IAQ, plumbing, electrical—to increase cross-sell attach rates and maximize lifetime value.',
  'Local champions are pursuing mergers-of-equals to dominate metro areas, consolidate marketing spend, and standardize systems before partnering with institutional capital.',
];

const prepChecklist = [
  'Rebuild P&amp;L to separate maintenance, service, replacement, and add-on trades.',
  'Document technician scorecards, NATE certifications, and training cadence.',
  'Provide membership lifecycle metrics—sign-ups, attrition, win-back campaigns.',
  'Compile call center scripts, booking rates, and dispatch cadences for peak season.',
  'Summarize marketing channels, cost per booked job, and campaign ROI.',
  'Outline supplier contracts, rebate programs, and preferred equipment line strategy.',
  'Map fleet health, telematics, and planned capex for trucks and tools.',
  'Prepare org chart depth, leadership succession, and incentive plans for key managers.',
];

const listItemClasses =
  'flex items-start gap-3 text-sm sm:text-base text-gray-700 leading-relaxed';
const listDotClasses =
  'mt-1.5 h-2 w-2 flex-none rounded-full bg-amiva-primary';

const visualShowcase = [
  {
    image: hvacHeroVisual,
    alt: 'Dispatch dashboard for HVAC technicians in the field.',
    label: 'Dispatch Lens',
    metric: '30% EBITDA lift',
    caption: 'Route density, conversion, and technician productivity in a single visual panel.',
    gradient: 'from-[#0B192E]/70 via-[#1D4ED8]/60 to-[#F97316]/60',
  },
  {
    image: membershipVisual,
    alt: 'Membership analytics showing recurring revenue and churn.',
    label: 'Membership Pulse',
    metric: '52% recurring mix',
    caption: 'Renewal, auto-pay, and upsell data packaged for buyer diligence calls.',
    gradient: 'from-[#12274A]/70 via-[#2563EB]/60 to-[#38BDF8]/60',
  },
  {
    image: retentionVisual,
    alt: 'Technician retention board with certification tracking.',
    label: 'Tech Retention',
    metric: '6 week sprint',
    caption: 'Scorecards elevate bench strength and remove succession anxiety for PE platforms.',
    gradient: 'from-[#0F172A]/70 via-[#9333EA]/60 to-[#10B981]/60',
  },
];

const highlightStats = [
  {
    label: 'Cash at Close',
    value: '68%',
    detail: 'median upfront consideration secured while rolling equity for platform upside.',
  },
  {
    label: 'Membership Mix Post-Sprint',
    value: '45%',
    detail: 'average portion of revenue attributable to recurring programs after readiness work.',
  },
  {
    label: 'Dispatch KPI Refresh',
    value: '24 hours',
    detail: 'time to push updated technician dashboards to buyers during diligence cadence.',
  },
];

const serviceSignals = [
  {
    title: 'Membership Engine Lens',
    summary: 'Auto-pay adoption, churn, and upsell probability mapped by territory.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="9" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
      </svg>
    ),
  },
  {
    title: 'Dispatch Efficiency Tile',
    summary: 'Route density, miles per job, and technician conversion visualized in minutes.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h11M4 18h9" />
        <circle cx="18" cy="18" r="2" fill="currentColor" />
      </svg>
    ),
  },
  {
    title: 'Tech Retention Dashboard',
    summary: 'Certifications, callbacks, and ticket conversion tracked for every lead tech.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12M6 9l6-3 6 3" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 15l6 3 6-3" />
      </svg>
    ),
  },
];

const processHighlights = [
  {
    title: 'Readiness Sprint',
    timeline: 'Weeks 1-2',
    items: ['Normalize cash-basis books', 'Segment memberships + seasonality', 'Catalog vendor + rebate programs'],
  },
  {
    title: 'Market Momentum',
    timeline: 'Weeks 3-5',
    items: ['Wave-based outreach', 'Technician spotlight reels', 'Route density visuals for buyers'],
  },
  {
    title: 'Offer Sculpting',
    timeline: 'Weeks 6-9',
    items: ['Model cash vs. rollover', 'Design leadership incentive plans', 'Plan integration guardrails'],
  },
];

const personaProfiles = [
  {
    title: 'Multi-Market HVAC Platforms',
    stat: '7+ territories',
    description:
      'Scaled operators balancing replacement, service, and membership revenue who need institutional capital while keeping dispatch velocity and culture intact.',
  },
  {
    title: 'HVAC + Plumbing / Electrical Combos',
    stat: 'Cross-trade bundles',
    description:
      'Home services leaders driving high attach rates across trades and seeking premium buyers that value bundled offerings and CRM discipline.',
  },
  {
    title: 'Succession & Partial Recap Plans',
    stat: 'Founder transition',
    description:
      'Owners elevating GMs and service managers while securing liquidity and staged rollovers aligned to growth targets.',
  },
];

const relatedPlaybooks = [
  {
    title: 'Manufacturing & Precision Machining M&A',
    description: 'For operators with fabrication, controls, or industrial service divisions complementing field work.',
    to: '/manufacturing-precision-machining-mna',
  },
  {
    title: 'SaaS & Software M&A Advisor',
    description: 'Ideal if your platform includes proprietary dispatch tech, membership portals, or analytics layers.',
    to: '/saas-mna-advisor',
  },
  {
    title: 'Dental Practice M&A Advisor',
    description: 'Membership economics and patient experience parallels that inspire service plan storytelling.',
    to: '/dentalpractice-mna-advisor',
  },
];

export default function HVACHomeServicesAdvisor() {
  return (
    <div className="bg-white">
      <SEOHead
        title="HVAC & Home Services M&A Advisor | AMIVA Equity Partners"
        description="Sell-side advisors for HVAC and home services companies. We highlight membership mix, route density, and technician productivity to run competitive, confidential processes."
        keywords={[
          'HVAC M&A advisor',
          'home services investment bank',
          'sell my HVAC company',
          'HVAC roll-up advisory',
        ]}
        canonical="/hvac-home-services-mna-advisor"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Industries', path: '/industries' },
          { name: 'HVAC & Home Services M&A Advisor', path: '/hvac-home-services-mna-advisor' },
        ]}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B192E] via-[#12274A] to-[#1B3A70] text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-[-6rem] -left-28 w-[28rem] h-[28rem] bg-amiva-primary/45 rounded-full blur-3xl"></div>
          <div className="absolute bottom-[-6rem] right-[-4rem] w-[30rem] h-[30rem] bg-amiva-accent/35 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,_3fr)_minmax(0,_2.4fr)] gap-12 items-center">
            <div className="max-w-3xl">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-6">
                HVAC &amp; Home Services M&amp;A Advisor
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
                Turn membership data into premium multiples
              </h1>
              <p className="text-base sm:text-lg text-gray-200 leading-relaxed mb-6">
                We rebuild cash-basis books, surface dispatch efficiency, and highlight technician
                depth so buyers see the recurring engine you have built. Roll-up sponsors, utilities,
                and strategics get the data they need without dragging your operators into nonstop
                meetings.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {heroMetrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-2xl border border-white/15 bg-white/5 p-4 text-center"
                  >
                    <div className="text-2xl font-semibold text-white">{metric.value}</div>
                    <p className="mt-1 text-xs uppercase tracking-[0.28em] text-white/70">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-[11px] leading-relaxed text-white/60">
                      {metric.description}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Link to="/valuation" className="btn-primary px-8 py-4 text-lg font-semibold">
                  Value My HVAC Company
                </Link>
                <a
                  href={appConfig.links.calendly}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-base font-semibold text-white hover:text-amiva-accent transition-colors"
                >
                  Book a confidential strategy call →
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-12 right-0 h-24 w-24 rounded-full bg-amiva-accent/40 blur-xl"></div>
              <div className="absolute bottom-[-2rem] -left-10 h-20 w-20 rounded-full bg-white/20 blur-lg"></div>
              <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/5 shadow-[0_30px_60px_rgba(11,25,46,0.45)]">
                <img
                  src={hvacHeroVisual}
                  alt="Technician dispatch board visualizing routes and membership calls."
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#12274A]/35 via-[#1B3A70]/10 to-transparent"></div>
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1 text-xs uppercase tracking-[0.32em] text-white">
                  Membership engine
                </div>
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                  <p className="text-sm font-semibold text-white">
                    Renewal rate 88% • <span className="text-white/80">dispatch miles ↓ 12%</span>
                  </p>
                  <p className="mt-1 text-xs text-white/70">
                    KPI overlays let buyers benchmark capacity before they step into your call center.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Founder Profiles */}
      <section className="py-16 lg:py-20 bg-gray-50" id="founder-profiles">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Founder Snapshot
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Home services leaders we help secure premium outcomes
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              The readiness plan adapts to your footprint—whether you are scaling multi-market
              HVAC fleets, bundling trades, or planning a succession handoff.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {personaProfiles.map((profile) => (
              <div
                key={profile.title}
                className="rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  {profile.stat}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{profile.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{profile.description}</p>
                <div className="text-xs text-gray-500">
                  We choreograph rollover, leadership incentives, and post-close roles before exclusivity.
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Field Visuals
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Give buyers the three dashboards that matter most
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Membership metrics, dispatch efficiency, and technician retention are the lifeblood
              of HVAC roll-ups. We package them into snackable visuals that drive valuation
              discussions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visualShowcase.map((shot) => (
              <div
                key={shot.label}
                className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-gray-900/5 shadow-lg"
              >
                <img
                  src={shot.image}
                  alt={shot.alt}
                  className="h-60 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${shot.gradient} opacity-80 mix-blend-multiply`}
                ></div>
                <div className="absolute inset-0 flex flex-col justify-between p-6">
                  <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/15 px-3 py-1 text-xs uppercase tracking-[0.28em] text-white">
                    {shot.label}
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
                    <div className="text-sm font-semibold text-white">{shot.metric}</div>
                    <p className="mt-1 text-xs text-white/75 leading-relaxed">{shot.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-gradient p-8 md:p-12 rounded-3xl border border-gray-100 shadow-xl">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-10">
              <div>
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-2">
                  Home services momentum
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Signals institutional HVAC buyers respond to immediately
                </h2>
              </div>
              <p className="text-sm md:text-base text-gray-600 max-w-xl">
                From membership depth to dispatch efficiency, we translate operating data into the
                metrics acquirers use when they stretch multiples.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {highlightStats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transition-transform duration-300 hover:-translate-y-1 flex flex-col gap-3 h-full"
                >
                  <div className="text-3xl sm:text-4xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amiva-primary">
                    {stat.label}
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              HVAC Performance Dashboard
            </h2>
            <p className="text-base text-gray-600">
              Field data converted to visual metrics
            </p>
          </div>

          {/* Visual HVAC Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-12">
            {[
              { icon: '🔄', label: 'Membership Penetration', value: 'Recurring Revenue', sublabel: 'Member count, renewal rates, auto-pay adoption' },
              { icon: '💰', label: 'Average Ticket by Call Type', value: 'Revenue Mix', sublabel: 'Service vs replacement vs add-on trades' },
              { icon: '📍', label: 'Route Density', value: 'Efficiency', sublabel: 'Average miles per job across territories' },
              { icon: '⚡', label: 'Technician Utilization', value: 'Productivity', sublabel: 'Billable hours, conversion rates, callbacks' },
              { icon: '🎯', label: 'Tune-up to Replacement', value: 'Conversion', sublabel: 'Financing adoption drives replacement sales' },
              { icon: '📞', label: 'Call Center Metrics', value: 'Booking Rate', sublabel: 'Answer speed, booking rates, data capture' },
              { icon: '👥', label: 'Technician Bench', value: 'Team Depth', sublabel: 'NATE certifications, tenure, training cadence' },
              { icon: '🔧', label: 'Callback Rate', value: 'Quality', sublabel: 'First-time fix rate shows operational maturity' },
              { icon: '💳', label: 'Financing Penetration', value: 'Enabler', sublabel: 'Third-party financing approval and usage rates' },
              { icon: '📈', label: 'Member Renewal Rate', value: 'Retention', sublabel: 'Annual membership churn and win-back campaigns' },
              { icon: '⭐', label: 'Customer Reviews', value: 'Reputation', sublabel: 'Google, Yelp scores drive referral economics' },
              { icon: '🏠', label: 'Market Coverage', value: 'Geography', sublabel: 'Territory density enables cross-sell leverage' }
            ].map((metric, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white to-orange-50 rounded-2xl border-2 border-orange-200 p-5 hover:border-orange-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="text-4xl mb-3 text-center">{metric.icon}</div>
                <div className="text-sm font-bold text-gray-900 mb-2 text-center">{metric.label}</div>
                <div className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-2 text-center">{metric.value}</div>
                <div className="text-xs text-gray-600 leading-snug">{metric.sublabel}</div>
              </div>
            ))}
          </div>

          {/* Visual Signal Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {serviceSignals.map((signal) => (
              <div key={signal.title} className="group relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50 rounded-3xl border-2 border-orange-200 p-8 hover:border-orange-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-200 rounded-full opacity-20 -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl mb-5 group-hover:scale-110 transition-transform duration-300">
                    {signal.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{signal.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{signal.summary}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
                Roll-up rhythm
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">HVAC Roll-Up Dynamics</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {processHighlights.map((phase) => (
                  <div key={phase.title} className="card-premium p-6 border border-gray-100">
                    <div className="text-xs font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-3">
                      {phase.timeline}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">{phase.title}</h3>
                    <ul className="space-y-2">
                      {phase.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amiva-primary"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-gradient p-8 shadow-xl transition-all duration-300 hover:-translate-y-1">
              <ul className="space-y-3">
                {rollupDynamics.map((item) => (
                  <li key={item} className={listItemClasses}>
                    <span className={listDotClasses}></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-500 mt-6">
                Heat maps track territory density, membership mix, and tech capacity so we line up buyers that can compound your playbook immediately.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Exit Prep: Visual Playbook
            </h2>
            <p className="text-base text-gray-600">
              8 workstreams with real-time tracking
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { num: '1', icon: '💰', title: 'P&L Rebuild: Maintenance, Service, Replacement, Add-on Trades', status: 'complete' },
              { num: '2', icon: '👨‍🔧', title: 'Technician Scorecards, NATE Certifications, Training Cadence', status: 'complete' },
              { num: '3', icon: '🔄', title: 'Membership Lifecycle: Sign-ups, Attrition, Win-back Campaigns', status: 'complete' },
              { num: '4', icon: '📞', title: 'Call Center Scripts, Booking Rates, Dispatch Cadences', status: 'progress' },
              { num: '5', icon: '🎯', title: 'Marketing Channels, Cost per Booked Job, Campaign ROI', status: 'progress' },
              { num: '6', icon: '🤝', title: 'Supplier Contracts, Rebate Programs, Equipment Line Strategy', status: 'pending' },
              { num: '7', icon: '🚛', title: 'Fleet Health, Telematics, Planned Capex for Trucks & Tools', status: 'pending' },
              { num: '8', icon: '👥', title: 'Org Chart Depth, Leadership Succession, Manager Incentives', status: 'pending' }
            ].map((item) => (
              <div
                key={item.num}
                className={`relative bg-white rounded-2xl border-2 p-6 transition-all duration-300 hover:-translate-y-1 ${
                  item.status === 'complete' ? 'border-green-300 bg-green-50/50' : 
                  item.status === 'progress' ? 'border-orange-300 bg-orange-50/50' : 
                  'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                    item.status === 'complete' ? 'bg-green-500 text-white' : 
                    item.status === 'progress' ? 'bg-orange-500 text-white' : 
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {item.num}
                  </span>
                  <span className="text-3xl">{item.icon}</span>
                </div>
                <h3 className="text-xs font-semibold text-gray-900 leading-tight">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">HVAC M&amp;A FAQs</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Seasoned HVAC operators ask the questions below before committing to a process. These
              answers give you a preview of how we handle the critical details.
            </p>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="card-premium p-6 md:p-8 transition-all duration-300 hover:-translate-y-1"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-amiva-primary transition-colors">
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Link */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Related Playbooks
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Explore complementary AMIVA coverage
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Field service operators often intersect with fabrication, healthcare, or software.
              These playbooks show how we apply the same rigor to adjacent growth paths.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPlaybooks.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="group rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1 hover:border-amiva-primary/40"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  Playbook
                </span>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-amiva-primary">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{item.description}</p>
                <span className="text-sm font-semibold text-amiva-primary group-hover:text-amiva-accent inline-flex items-center gap-2">
                  View details
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 10.5L10.5 3.5M10.5 3.5H4.375M10.5 3.5V9.625"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-amiva-primary to-amiva-accent text-white py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">Plot the next chapter for your HVAC brand.</h2>
            <p className="text-lg text-white/90">
              We will translate your KPIs into a market-ready story, align on timing, and activate
              the buyers willing to pay top-tier multiples.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/valuation" className="bg-white text-amiva-primary font-semibold px-6 py-3 rounded-xl text-center">
              Start the Valuation
            </Link>
            <a
              href={appConfig.links.calendly}
              target="_blank"
              rel="noreferrer"
              className="border border-white/70 rounded-xl px-6 py-3 font-semibold text-center hover:bg-white/10 transition"
            >
              Schedule a Strategy Call
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
